/**
 * Canonical lookback window types for the Opteo monorepo.
 *
 * This module provides the single source of truth for all lookback window
 * related types, presets, and utility functions.
 *
 * Usage: import { LookbackWindow } from '@opteo/types'
 *   - LookbackWindow.LAST_30_DAYS (enum value)
 *   - LookbackWindow.STANDARD_LOOKBACKS (constant)
 *   - LookbackWindow.isMonthToDate(window) (function)
 */

/**
 * LookbackWindow namespace containing the enum, presets, and utilities.
 * Uses TypeScript declaration merging to allow direct access like LookbackWindow.LAST_30_DAYS
 */
export enum LookbackWindow {
    LAST_7_DAYS = 'last7Days',
    LAST_14_DAYS = 'last14Days',
    LAST_30_DAYS = 'last30Days',
    LAST_45_DAYS = 'last45Days',
    LAST_60_DAYS = 'last60Days',
    LAST_90_DAYS = 'last90Days',
    LAST_180_DAYS = 'last180Days',
    LAST_365_DAYS = 'last365Days',
    LAST_730_DAYS = 'last730Days',
    MONTH_TO_DATE = 'monthToDate',
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace LookbackWindow {
    // ============================================================================
    // DAYS MAPPING
    // ============================================================================

    /**
     * Maps each LookbackWindow to its corresponding number of days.
     * MONTH_TO_DATE is special-cased with a string literal.
     */
    export const LOOKBACK_WINDOW_TO_DAYS: Record<LookbackWindow, number | 'monthToDate'> = {
        [LookbackWindow.LAST_7_DAYS]: 7,
        [LookbackWindow.LAST_14_DAYS]: 14,
        [LookbackWindow.LAST_30_DAYS]: 30,
        [LookbackWindow.LAST_45_DAYS]: 45,
        [LookbackWindow.LAST_60_DAYS]: 60,
        [LookbackWindow.LAST_90_DAYS]: 90,
        [LookbackWindow.LAST_180_DAYS]: 180,
        [LookbackWindow.LAST_365_DAYS]: 365,
        [LookbackWindow.LAST_730_DAYS]: 730,
        [LookbackWindow.MONTH_TO_DATE]: 'monthToDate',
    } as const

    // ============================================================================
    // PRESETS - Named subsets for common use cases
    // ============================================================================

    /**
     * STANDARD - Core lookback windows without MTD.
     * Used by: segments, base warehouse calculations
     */
    export const STANDARD_LOOKBACKS: readonly [
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_90_DAYS,
        LookbackWindow.LAST_180_DAYS,
        LookbackWindow.LAST_365_DAYS,
    ] = [
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_90_DAYS,
        LookbackWindow.LAST_180_DAYS,
        LookbackWindow.LAST_365_DAYS,
    ] as const
    export type StandardLookback = (typeof STANDARD_LOOKBACKS)[number]

    /**
     * STANDARD_WITH_MTD - Standard lookbacks plus Month-to-Date.
     * Used by: keywords metrics, ngram tool, cpc bands
     */
    export const STANDARD_WITH_MTD_LOOKBACKS: readonly [
        LookbackWindow.MONTH_TO_DATE,
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_90_DAYS,
        LookbackWindow.LAST_180_DAYS,
        LookbackWindow.LAST_365_DAYS,
    ] = [
        LookbackWindow.MONTH_TO_DATE,
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_90_DAYS,
        LookbackWindow.LAST_180_DAYS,
        LookbackWindow.LAST_365_DAYS,
    ] as const
    export type StandardWithMtdLookback = (typeof STANDARD_WITH_MTD_LOOKBACKS)[number]

    /**
     * EXTENDED - Standard lookbacks with additional 730 days for MS ads.
     * Used by: Microsoft keywords metrics (requires 2-year data)
     */
    export const EXTENDED_LOOKBACKS: readonly [
        LookbackWindow.MONTH_TO_DATE,
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_90_DAYS,
        LookbackWindow.LAST_180_DAYS,
        LookbackWindow.LAST_365_DAYS,
        LookbackWindow.LAST_730_DAYS,
    ] = [
        LookbackWindow.MONTH_TO_DATE,
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_90_DAYS,
        LookbackWindow.LAST_180_DAYS,
        LookbackWindow.LAST_365_DAYS,
        LookbackWindow.LAST_730_DAYS,
    ] as const
    export type ExtendedLookback = (typeof EXTENDED_LOOKBACKS)[number]

    /**
     * PLACEMENT - Used for placement excluder tool.
     */
    export const PLACEMENT_LOOKBACKS: readonly [
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_90_DAYS,
    ] = [LookbackWindow.LAST_30_DAYS, LookbackWindow.LAST_90_DAYS] as const
    export type PlacementLookback = (typeof PLACEMENT_LOOKBACKS)[number]

    /**
     * IMPROVEMENT - Short-term windows for improvement calculations.
     * Used by: keyword bids, demo bids, device bids (7-90 day focus)
     */
    export const IMPROVEMENT_LOOKBACKS: readonly [
        LookbackWindow.LAST_7_DAYS,
        LookbackWindow.LAST_14_DAYS,
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_45_DAYS,
        LookbackWindow.LAST_60_DAYS,
        LookbackWindow.LAST_90_DAYS,
    ] = [
        LookbackWindow.LAST_7_DAYS,
        LookbackWindow.LAST_14_DAYS,
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_45_DAYS,
        LookbackWindow.LAST_60_DAYS,
        LookbackWindow.LAST_90_DAYS,
    ] as const
    export type ImprovementLookback = (typeof IMPROVEMENT_LOOKBACKS)[number]

    /**
     * LEGACY_IMPROVEMENT - Exact match for the deprecated Improvement.LookbackWindow enum.
     * Used during migration from numeric to string-based lookback windows.
     * Contains: 7, 14, 30, 45, 60, 90, 180, 365 days (no MTD, no 730)
     *
     * @deprecated Prefer using more specific presets like IMPROVEMENT_LOOKBACKS or STANDARD_LOOKBACKS
     */
    export const LEGACY_IMPROVEMENT_LOOKBACKS: readonly [
        LookbackWindow.LAST_7_DAYS,
        LookbackWindow.LAST_14_DAYS,
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_45_DAYS,
        LookbackWindow.LAST_60_DAYS,
        LookbackWindow.LAST_90_DAYS,
        LookbackWindow.LAST_180_DAYS,
        LookbackWindow.LAST_365_DAYS,
    ] = [
        LookbackWindow.LAST_7_DAYS,
        LookbackWindow.LAST_14_DAYS,
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_45_DAYS,
        LookbackWindow.LAST_60_DAYS,
        LookbackWindow.LAST_90_DAYS,
        LookbackWindow.LAST_180_DAYS,
        LookbackWindow.LAST_365_DAYS,
    ] as const
    export type LegacyImprovementLookback = (typeof LEGACY_IMPROVEMENT_LOOKBACKS)[number]

    /**
     * ACCOUNT_MANAGER - API lookback windows for account manager.
     * Used by: GetAccountData, GetAccountSubEntityData APIs
     */
    export const ACCOUNT_MANAGER_LOOKBACKS: readonly [
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_90_DAYS,
        LookbackWindow.LAST_180_DAYS,
        LookbackWindow.LAST_365_DAYS,
        LookbackWindow.MONTH_TO_DATE,
    ] = [
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_90_DAYS,
        LookbackWindow.LAST_180_DAYS,
        LookbackWindow.LAST_365_DAYS,
        LookbackWindow.MONTH_TO_DATE,
    ] as const
    export type AccountManagerLookback = (typeof ACCOUNT_MANAGER_LOOKBACKS)[number]

    /**
     * COMPUTE_RANGE - Windows used by getComputeRanges for metrics aggregation.
     */
    export const COMPUTE_RANGE_LOOKBACKS: readonly [
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_90_DAYS,
        LookbackWindow.LAST_180_DAYS,
        LookbackWindow.LAST_365_DAYS,
        LookbackWindow.MONTH_TO_DATE,
    ] = [
        LookbackWindow.LAST_30_DAYS,
        LookbackWindow.LAST_90_DAYS,
        LookbackWindow.LAST_180_DAYS,
        LookbackWindow.LAST_365_DAYS,
        LookbackWindow.MONTH_TO_DATE,
    ] as const
    export type ComputeRangeLookback = (typeof COMPUTE_RANGE_LOOKBACKS)[number]
    export type ComputeRangeWindow = ComputeRangeLookback

    // ============================================================================
    // TYPE GUARDS AND UTILITIES
    // ============================================================================

    const lookbackWindowValues = new Set(Object.values(LookbackWindow))

    /**
     * Type guard to check if a value is a valid LookbackWindow.
     */
    export function isLookbackWindow(value: unknown): value is LookbackWindow {
        return typeof value === 'string' && lookbackWindowValues.has(value as LookbackWindow)
    }

    /**
     * Gets the numeric day count for a lookback window.
     * Returns undefined for MONTH_TO_DATE.
     */
    export function toDays(window: StandardLookback): number
    export function toDays(window: LookbackWindow): number | undefined
    export function toDays(window: LookbackWindow): number | undefined {
        const days = LOOKBACK_WINDOW_TO_DAYS[window]
        return typeof days === 'number' ? days : undefined
    }

    /**
     * Checks if a lookback window is month-to-date.
     */
    export function isMonthToDate(window: LookbackWindow): window is LookbackWindow.MONTH_TO_DATE {
        return window === LookbackWindow.MONTH_TO_DATE
    }
}
